import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { AuthService, BreadcrumbService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { Router } from '@angular/router';
import { AddQueueRequest, IQueueDetails, ListQueuesRequest, QueueDetails, SituAnalyticsWebApiClient } from 'situanalytics-webapi-client';
import { MatDialog } from '@angular/material/dialog';
import { AddQueueDialogComponent } from '../add-queue-dialog/add-queue-dialog.component';

@Component({
  selector: 'app-queue-list',
  templateUrl: './queue-list.component.html',
  styleUrls: ['./queue-list.component.scss']
})
export class QueueListComponent implements OnInit {
  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100],
    displayedColumns: ['name']
  } as ListViewControls;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  previousSearchTerm = "";
  searchTerm = "";
  admin = false;
  queuesDataSource = new MatTableDataSource<IQueueDetails>();

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private analyticsApi: SituAnalyticsWebApiClient,
    private authService: AuthService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    public dialog: MatDialog) {
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
    this.loadQueues();
  }

  ngOnInit(): void { }

  onClickAddQueue() {
    // open dialog to get name
    this.dialog.open(AddQueueDialogComponent, {
      data: {
        existingNames: this.queuesDataSource.data.map(x => { return x.name; }),
      }
    }).afterClosed().subscribe((name: string) => {
      let request = new AddQueueRequest();
      request.name = name;

      return this.analyticsApi.addQueue(request).then(response => {
        if(response.success){
          let queue = new QueueDetails();
          queue.id = response.id;
          queue.name = name;
          queue.recipeEventSummaries = [];
          queue.rendererQueues = [];

          this.queuesDataSource.data.splice(0, 0, queue);
        }
      }).finally(() => {
        this.loading = false;
      });
    });
  }

  loadQueues() {
    this.loading = true;

    let request = new ListQueuesRequest();

    if (this.searchTermChanged()) this.resetSearch();
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    request.searchTerm = this.searchTerm;
    this.previousSearchTerm = this.searchTerm;

    return this.analyticsApi.listQueues(request).then(response => {
      this.queuesDataSource.data = response.items?.sort(this.sortByName) ?? [];
      this.viewControls.totalItems = response.totalItems ?? 0;
    }).finally(() => {
      this.loading = false;
    });
  }

  private sortByName(a: IQueueDetails, b: IQueueDetails) {
    return (a.name ?? '').localeCompare(b.name ?? '');
  }

  onViewQueue(queueId: string) {
    this.router.navigate([`/admin/queues/${queueId}/dashboard`]);
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadQueues();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  getQueueCount(queue: QueueDetails){
    return queue.recipeEventSummaries?.filter(x => !x.dateCancelled && !x.dateStarted).length;
  }

  private searchTermChanged(): boolean {
    return this.searchTerm.toLowerCase() !== this.previousSearchTerm.toLowerCase();
  }
}

interface ListViewControls {
  pageSize: number;
  pageIndex: number;
  pageNumber: number;
  totalItems: number;
  pageSizeOptions: number[];
  displayedColumns: string[];
}
